import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Bills, fileToBase64 } from "@/lib/api";
import {
  fmtDate,
  fmtMoney,
  STATUS_META,
  statusChipClass,
} from "@/lib/bill-utils";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Loader2, ArrowRight, Paperclip } from "lucide-react";
import { toast } from "sonner";

const Row = ({ label, value, bold }) => (
  <div className="flex items-baseline justify-between py-2 border-b border-neutral-200">
    <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 font-bold">
      {label}
    </div>
    <div
      className={`${bold ? "font-display font-extrabold text-lg" : "text-sm"} mono`}
    >
      {value}
    </div>
  </div>
);

const PublicPay = () => {
  const { id } = useParams();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("");
  const [note, setNote] = useState("");
  const [payFull, setPayFull] = useState(true);
  const [receiptNumber, setReceiptNumber] = useState("");
  const [receiptBase64, setReceiptBase64] = useState(null);
  const [receiptMime, setReceiptMime] = useState(null);
  const [receiptName, setReceiptName] = useState("");
  const receiptInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const b = await Bills.get(id);
      setBill(b);
      if (b.remaining_balance != null) {
        setAmount(b.remaining_balance.toFixed(2));
      }
    } catch {
      toast.error("Bill not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onFullChange = (checked) => {
    setPayFull(checked);
    if (checked && bill) setAmount(bill.remaining_balance.toFixed(2));
  };

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setBusy(true);
    try {
      const updated = await Bills.addPayment(id, {
        amount: amt,
        method: method || undefined,
        note: note || "Paid from email link",
        bank_receipt_number: receiptNumber || undefined,
        receipt_image_base64: receiptBase64 || undefined,
        receipt_image_mime: receiptMime || undefined,
      });
      setBill(updated);
      setDone(true);
    } catch {
      toast.error("Could not record payment");
    } finally {
      setBusy(false);
    }
  };

  const onPickReceipt = async (file) => {
    if (!file) return;
    try {
      const { base64, mime } = await fileToBase64(file);
      setReceiptBase64(base64);
      setReceiptMime(mime);
      setReceiptName(file.name);
    } catch {
      toast.error("Could not read that file.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex items-center gap-3 text-neutral-600">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading bill…
        </div>
      </div>
    );
  }

  if (!bill) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="border-2 border-black p-8 max-w-md w-full">
          <div className="swiss-label mb-2">404</div>
          <div className="font-display font-black text-3xl">Bill not found</div>
          <p className="text-neutral-600 mt-3 text-sm">
            The link may be incorrect or the bill was deleted.
          </p>
        </div>
      </div>
    );
  }

  const meta = STATUS_META[bill.status] || STATUS_META.unpaid;

  return (
    <div className="min-h-screen bg-[var(--bg)] py-10 px-5">
      <div className="max-w-xl mx-auto">
        <div className="mb-6">
          <div className="swiss-label">Billy · Payment</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-2">
            {done ? "Payment recorded" : "Mark bill as paid"}
          </h1>
        </div>

        <div
          data-testid="public-pay-summary"
          className="bg-white border-2 border-black p-6 md:p-8 mb-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <span className={`chip ${statusChipClass(bill.status)}`}>
              {meta.label}
            </span>
          </div>
          <div className="swiss-label">Company</div>
          <div className="font-display font-extrabold text-2xl mt-1">
            {bill.company}
          </div>
          <div className="hash text-sm text-neutral-500 mt-1">
            # {bill.invoice_number}
          </div>

          <div className="mt-5 space-y-0">
            <Row label="Amount Due" value={fmtMoney(bill.amount_due)} />
            <Row label="Already Paid" value={fmtMoney(bill.total_paid)} />
            <Row
              label="Remaining"
              value={fmtMoney(bill.remaining_balance)}
              bold
            />
            <Row label="Due Date" value={fmtDate(bill.due_date)} />
          </div>

          {(bill.account_name ||
            bill.bank_name ||
            bill.bsb ||
            bill.account_number ||
            bill.bpay_biller_code ||
            bill.payid ||
            bill.payment_url ||
            bill.accepted_methods) && (
            <div className="mt-6 pt-4 border-t border-neutral-200">
              <div className="swiss-label mb-2">Pay Into</div>
              <div className="text-sm space-y-1">
                {bill.account_name && (
                  <div>
                    <span className="text-neutral-500">Account: </span>
                    {bill.account_name}
                  </div>
                )}
                {bill.bank_name && (
                  <div>
                    <span className="text-neutral-500">Bank: </span>
                    {bill.bank_name}
                  </div>
                )}
                {bill.bsb && (
                  <div>
                    <span className="text-neutral-500">BSB: </span>
                    <span className="mono">{bill.bsb}</span>
                  </div>
                )}
                {bill.account_number && (
                  <div>
                    <span className="text-neutral-500">Account No.: </span>
                    <span className="mono">{bill.account_number}</span>
                  </div>
                )}
                {(bill.payment_reference || bill.invoice_number) && (
                  <div>
                    <span className="text-neutral-500">Reference: </span>
                    {bill.payment_reference || bill.invoice_number}
                  </div>
                )}
              </div>

              {(bill.bpay_biller_code || bill.bpay_reference) && (
                <div className="mt-3 pt-3 border-t border-neutral-100 text-sm space-y-1">
                  <div className="swiss-label mb-1">BPAY</div>
                  <div>
                    <span className="text-neutral-500">Biller Code: </span>
                    <span className="mono">{bill.bpay_biller_code || "—"}</span>
                  </div>
                  <div>
                    <span className="text-neutral-500">Reference: </span>
                    <span className="mono break-all">
                      {bill.bpay_reference || "—"}
                    </span>
                  </div>
                </div>
              )}

              {bill.payid && (
                <div className="mt-3 pt-3 border-t border-neutral-100 text-sm">
                  <div className="swiss-label mb-1">PayID</div>
                  <div className="break-all">{bill.payid}</div>
                </div>
              )}

              {bill.payment_url && (
                <div className="mt-3 pt-3 border-t border-neutral-100 text-sm">
                  <div className="swiss-label mb-1">Pay Online</div>
                  <a
                    href={bill.payment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-black break-all"
                  >
                    {bill.payment_url}
                  </a>
                </div>
              )}

              {bill.accepted_methods && (
                <div className="mt-3 pt-3 border-t border-neutral-100 text-sm">
                  <div className="swiss-label mb-1">Other methods</div>
                  <div>{bill.accepted_methods}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {done ? (
          <div
            data-testid="public-pay-success"
            className="bg-white border-2 border-green-600 p-6 md:p-8"
          >
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-700 flex-shrink-0" />
              <div>
                <div className="font-display font-extrabold text-xl">
                  {bill.status === "paid"
                    ? "Bill marked as fully paid."
                    : "Partial payment recorded."}
                </div>
                <div className="text-sm text-neutral-600 mt-1">
                  Remaining balance:{" "}
                  <span className="mono font-bold">
                    {fmtMoney(bill.remaining_balance)}
                  </span>
                </div>
                <div className="text-xs text-neutral-500 mt-3">
                  Thanks — you can close this tab.
                </div>
              </div>
            </div>
          </div>
        ) : bill.status === "paid" ? (
          <div className="bg-white border-2 border-green-600 p-6 md:p-8">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-700 flex-shrink-0" />
              <div>
                <div className="font-display font-extrabold text-xl">
                  This bill is already fully paid.
                </div>
                <div className="text-sm text-neutral-600 mt-1">
                  Nothing further to do.
                </div>
              </div>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            data-testid="public-pay-form"
            className="bg-white border-2 border-black p-6 md:p-8 space-y-5"
          >
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                data-testid="pay-full-toggle"
                type="checkbox"
                checked={payFull}
                onChange={(e) => onFullChange(e.target.checked)}
                className="h-5 w-5 accent-black"
              />
              <span className="text-sm font-bold uppercase tracking-widest">
                Paid in full ({fmtMoney(bill.remaining_balance)})
              </span>
            </label>

            <label className="block">
              <div className="swiss-label mb-1">Amount Paid (AUD)</div>
              <Input
                data-testid="pay-amount"
                className="swiss-input mono text-xl"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  setPayFull(false);
                }}
                inputMode="decimal"
                placeholder="0.00"
              />
            </label>

            <label className="block">
              <div className="swiss-label mb-1">Method (optional)</div>
              <Input
                data-testid="pay-method"
                className="swiss-input"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                placeholder="Bank transfer, card, cash…"
              />
            </label>

            <label className="block">
              <div className="swiss-label mb-1">
                Bank Receipt Number (optional)
              </div>
              <Input
                data-testid="pay-receipt-number"
                className="swiss-input mono"
                value={receiptNumber}
                onChange={(e) => setReceiptNumber(e.target.value)}
                placeholder="e.g. NAB confirmation #123456789"
              />
            </label>

            <div>
              <div className="swiss-label mb-2">
                Bank Receipt (optional — image or PDF)
              </div>
              <input
                ref={receiptInputRef}
                data-testid="pay-receipt-file"
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => onPickReceipt(e.target.files?.[0])}
              />
              <button
                type="button"
                data-testid="pay-receipt-upload"
                onClick={() => receiptInputRef.current?.click()}
                className="w-full border-2 border-dashed border-neutral-300 hover:border-black bg-white text-neutral-700 font-bold uppercase tracking-widest text-xs px-4 py-4 transition-colors inline-flex items-center justify-center gap-2"
              >
                <Paperclip className="h-4 w-4" />
                {receiptName ? receiptName : "Attach bank receipt"}
              </button>
              {receiptBase64 && (
                <button
                  type="button"
                  onClick={() => {
                    setReceiptBase64(null);
                    setReceiptMime(null);
                    setReceiptName("");
                  }}
                  className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-red-600 mt-2"
                >
                  Remove attachment
                </button>
              )}
            </div>

            <label className="block">
              <div className="swiss-label mb-1">Note (optional)</div>
              <Textarea
                data-testid="pay-note"
                className="swiss-input resize-none"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Transfer ref ABC-1234"
              />
            </label>

            <button
              type="submit"
              data-testid="pay-submit"
              disabled={busy}
              className="w-full border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-6 py-4 hover:bg-white hover:text-black transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
              Record Payment
            </button>
          </form>
        )}

        <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 text-center mt-8">
          Billy — Bill Reminders
        </div>
      </div>
    </div>
  );
};

export default PublicPay;
